/**
 * Job and clinics (Aziz 2026-09-29): the one place an owner or manager says what a
 * person approves. Their job (Art director or Content creator) brings its approval
 * seats; ticking a clinic makes them the holder there, taking over from whoever held
 * it, and moves what already waits on it to them. A new content creator joining is
 * one visit here, no code.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Stamp } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { fetchJobSeats, saveJobSeats } from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';

export default function JobClinics({ userId, jobDirty }: { userId: string; jobDirty: boolean }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const q = useQuery({ queryKey: ['job-seats', userId], queryFn: () => fetchJobSeats(userId) });
  const [on, setOn] = useState<Set<string>>(new Set());
  const [alone, setAlone] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!q.data) return;
    setOn(new Set(q.data.clinics.filter((c) => c.on_job).map((c) => c.client_id)));
    setAlone(new Set(q.data.clinics.filter((c) => c.one_person).map((c) => c.client_id)));
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => saveJobSeats(userId, Array.from(on), Array.from(alone)),
    onSuccess: () => { toast.success('Saved. Their approvals follow on those clinics.'); qc.invalidateQueries({ queryKey: ['job-seats', userId] }); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not save'),
  });
  const d = q.data;
  if (!d) return null;
  const first = d.display_name.split(' ')[0];
  const flip = (set: Set<string>, id: string, put: (s: Set<string>) => void) => {
    const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); put(next);
  };

  return (
    <Card className="space-y-3" data-testid="job-clinics">
      <div className="flex items-center gap-2">
        <Stamp size={14} className="text-accent-400" />
        <h3 className="font-medium text-muted-strong text-sm">Job and clinics</h3>
        {d.job_label && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-accent-600/20 text-accent-400" data-testid="job-label">{d.job_label}</span>}
      </div>
      {!d.job ? (
        <p className="text-xs text-muted">Pick Art director or Content creator in Designations and save; then choose the clinics here.</p>
      ) : jobDirty ? (
        <p className="text-xs text-amber-300">Save the job change first, then choose the clinics.</p>
      ) : (
        <>
          <p className="text-xs text-muted">
            On each ticked clinic {first} decides: {d.seats.join('; ')}. Ticking a clinic takes those over from whoever holds
            them; anything already waiting there moves to {first}. Owners and managers can still approve everything.
          </p>
          <ul className="divide-y divide-slate-800 border border-slate-800 rounded-lg">
            {d.clinics.map((c) => {
              const checked = on.has(c.client_id);
              const holders = Array.from(new Set(Object.values(c.holders).flat()));
              const others = holders.filter((n) => n !== d.display_name);
              return (
                <li key={c.client_id} className="px-3 py-2.5 flex items-start gap-3" data-testid="job-clinic">
                  <input type="checkbox" className="mt-1 accent-sky-500" checked={checked} data-testid="job-clinic-check"
                    onChange={() => flip(on, c.client_id, setOn)} aria-label={`${first} is the ${d.job_label?.toLowerCase()} on ${c.name}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-muted-strong">{c.name}</div>
                    <div className={cn('text-[11px]', checked && others.length ? 'text-amber-300' : 'text-muted')}>
                      {holders.length === 0 ? 'Nobody named yet'
                        : checked && others.length ? `Takes over from ${others.join(', ')}`
                        : `Today: ${holders.join(', ')}`}
                    </div>
                    {me?.role === 'owner' && checked && (
                      <label className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-muted cursor-pointer">
                        <input type="checkbox" className="accent-amber-500" checked={alone.has(c.client_id)} onChange={() => flip(alone, c.client_id, setAlone)} data-testid="job-one-person" />
                        One-person clinic: {first} may also hold the other side of the review here
                      </label>
                    )}
                  </div>
                </li>
              );
            })}
            {d.clinics.length === 0 && <li className="px-3 py-2.5 text-xs text-muted">Give {first} access to a clinic first.</li>}
          </ul>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} data-testid="job-clinics-save">Save clinics</Button>
          </div>
        </>
      )}
    </Card>
  );
}
